// Multi-tenant helper. The client (tenant) is the FIRST path segment of the
// URL: folio.alicewonders.ws/{slug}/home -> slug. One build serves every
// client; the slug decides which client's data + branding is loaded.

/**
 * The current client slug, or null when the URL has no leading segment
 * (bare "/", which should render a "no tenant" landing).
 *
 * @returns {string|null}
 */
export function getSlug() {
  const segment = window.location.pathname.split("/").filter(Boolean)[0];
  return segment || null;
}

/**
 * Whether a client slug is present in the URL.
 *
 * @returns {boolean}
 */
export function hasTenant() {
  return getSlug() !== null;
}

// A Magic Folio box (a touch PC the Magic Platform provisions) opens the
// tenant page with ?device={key}. The key is remembered per tenant so the
// SPA's own navigation (no query string) keeps identifying the box, and the
// public API is called with it: Folio registers the box and may limit the
// empreendimentos it shows. A plain visitor has no key.
const DEVICE_STORAGE_PREFIX = "magicFolioDevice:";

/**
 * The device key of this box, or null.
 *
 * @returns {string|null}
 */
export function getDeviceKey() {
  const slug = getSlug();
  if (!slug) return null;
  const storageKey = DEVICE_STORAGE_PREFIX + slug;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get("device");
    if (fromUrl && /^[A-Za-z0-9_-]{1,64}$/.test(fromUrl)) {
      localStorage.setItem(storageKey, fromUrl);
      return fromUrl;
    }
    return localStorage.getItem(storageKey) || null;
  } catch (e) {
    return null;
  }
}

/**
 * Whether this page runs on a Magic Folio box (device key present).
 *
 * @returns {boolean}
 */
export function isDevice() {
  return getDeviceKey() !== null;
}

// Offline mode (service worker + persistent storage) is opt-in: a Magic
// Folio box has it by its device key; a visitor's phone never gets it. For
// testing in a plain browser: `localStorage.magicFolioOffline = "1"` (reload).
export function offlineEnabled() {
  if (isDevice()) return true;
  try {
    return localStorage.getItem("magicFolioOffline") === "1";
  } catch (e) {
    return false;
  }
}
